import { LoaderFunction, type MetaFunction, defer } from "@remix-run/node";
import { useEffect, useRef, useState, lazy, Suspense, useCallback } from "react";
import { useLoaderData } from "@remix-run/react";
import { Card } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";

import { getCountryName } from "~/lib/helpers/constants";
import { getDeviceInfo } from "~/lib/hooks/useViewportListener";
import { DEFAULT_REGION } from "~/lib/constants/generalConstants";
import BlogsService from "~/lib/services/BlogsService";

const BlogCard = lazy(() => import("~/components/blocks/Card/BlogCard"));
import DesktopLayout from "~/components/blocks/BlogsPage/DesktopLayout";
import MobileLayout from "~/components/blocks/BlogsPage/MobileLayout";
import { newsParentMeta } from "~/utils/Meta/newsParentMeta";
import { getConfig, regionConfigs } from "custom-config/region.configs";

export const meta: MetaFunction = ({ data, location }) => {
  if (!data || !location) return [];
  return newsParentMeta({ data: data as any, path: location.pathname || '/' });
};

export const loader: LoaderFunction = async ({ params, request }) => {
  const { isMobile } = getDeviceInfo(request);

  const countryName = getCountryName(params.country || DEFAULT_REGION);
  const region = params.country || DEFAULT_REGION;
  const regionData = getConfig(region as keyof typeof regionConfigs) ?? undefined;
  // Fetch immediately needed content
  const content = await BlogsService.fetchNewsBlogs(isMobile ? 5 : 12, countryName);

  // Defer these
  const blogsPromise = BlogsService.fetchBlogsByCategories(1, isMobile ? 8 : 12, countryName, "blog", "");
  const mediaPromise = BlogsService.fetchBlogMedia(countryName);

  return defer({
    content: content.data,
    blogs: blogsPromise, // deferred
    media: mediaPromise, // deferred
    country: countryName,
    params,
    isMobile,
    region,
    regionData
  });
};

export default function Index() {
  const { country, params, isMobile, content, media, blogs, region } = useLoaderData<typeof loader>();
  const [currentPage, setCurrentPage] = useState(1);
  const [blogsData, setBlogsData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currentMedia, setCurrentMedia] = useState<any>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const ITEMS_PER_PAGE = isMobile ? 5 : 12;

  const getBlogs = useCallback(
    async (categories: string[], search: string, page = 1) => {
      setLoading(true);
      const blogs = await BlogsService.fetchBlogsByCategories(
        page,
        ITEMS_PER_PAGE,
        country,
        "blog",
        "",
        search,
        ""
      );
      setBlogsData(blogs);
      setLoading(false);
    },
    [ITEMS_PER_PAGE, country]
  );

  useEffect(() => {
    // Wait for deferred blogs to resolve
    Promise.resolve(blogs).then(setBlogsData);
  }, [blogs]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      getBlogs([], value);
    }, 1000); // Reduced to 1s for better UX
  };

  const renderBlogs = () => {
    if (loading) {
      return Array.from({ length: ITEMS_PER_PAGE }, (_, idx) => (
        <Card key={idx} className="min-w-[250px] w-full rounded-xl shadow-sm">
          <Skeleton className="min-w-[250px] h-48 w-full" />
        </Card>
      ));
    }
    if (!blogsData?.blogs?.length) {
      return (
        <div className="flex flex-col justify-center items-center mt-10 mb-10 text-center text-gray-800 text-sm gap-3 w-full col-span-full">
          <p className="font-medium text-base">No results</p>
          <p>Please change your search criteria and try again</p>
        </div>
      );
    }
    // If blogs exist
    return blogsData?.blogs.map((article: any, idx: number) => (
      <Suspense fallback={<Skeleton className="h-48 w-full" />} key={article.id || idx}>
        <BlogCard article={article} country={country} />
      </Suspense>
    ));
  };

  return isMobile ? (
    <MobileLayout
      renderBlogs={renderBlogs}
      content={content}
      onSearch={onSearch}
      media={media}
      currentMedia={currentMedia}
      setCurrentMedia={setCurrentMedia}
      blogsData={blogsData}
      currentPage={currentPage}
      getBlogs={getBlogs}
      searchText={searchText}
      setCurrentPage={setCurrentPage}
      params={params}
      country={country}
      loading={loading}
    />
  ) : (
    <DesktopLayout
      getBlogs={getBlogs}
      renderBlogs={renderBlogs}
      params={params}
      content={content}
      onSearch={onSearch}
      media={media}
      currentMedia={currentMedia}
      setCurrentMedia={setCurrentMedia}
      searchText={searchText}
      blogsData={blogsData}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      country={country}
      region={region}
      loading={loading}
    />
  );
}
